import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  CloseButton,
  Flex,
  NumberInput,
  NumberInputField,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import {FilterSelect} from './FilterSelect';

interface NumberRange {
  min?: number;
  max?: number;
}
const filterSelectionOptions = {
  // eq: {label: '=', value: 'eq'},
  // neq: {label: '≠', value: 'neq'},
  // gt: {label: '>;', value: 'gt'},
  // lt: {label: '<', value: 'lt'},
  // gte: {label: '≥', value: 'gte'},
  // lte: {label: '≤', value: 'lte'},
  btw: {label: 'between', value: 'btw'},
};

export const NumberFilterControl = ({
  min,
  max,
  onMinChange,
  onMaxChange,
  onRemove,
}: {
  label?: string;
  min?: number;
  max?: number;
  onMinChange: (value?: number) => void;
  onMaxChange: (value?: number) => void;
  onRemove?: () => void;
}) => {
  const [range, setRange] = useState<NumberRange>({min, max});

  // Ensure that the local range state stays in sync with prop changes
  useEffect(() => {
    setRange({min, max});
  }, [min, max]);

  // Apply local changes after debounce period to prevent too many updates
  const persistChanges = useMemo(
    () =>
      _.debounce((updatedRange: NumberRange) => {
        onMinChange(updatedRange.min);
        onMaxChange(updatedRange.max);
      }, 250),
    [onMinChange, onMaxChange]
  );

  // Clean up any pending debounced updates when unmounting.
  // This prevents stale updates from being applied after the component is removed.
  // Example: If a user is typing in an input field and the filter is removed,
  // the input's blur event could trigger a final debounced update with outdated values.
  useEffect(() => {
    return () => {
      persistChanges.cancel();
    };
  }, [persistChanges]);

  const onMinValueChanged = useCallback(
    (value: string) => {
      const num = parseInt(value);
      const updatedValue = _.isFinite(num) ? num : undefined;
      setRange((prevState) => {
        const newState: NumberRange = {
          ...prevState,
          min: updatedValue,
        };
        persistChanges(newState);
        return newState;
      });
    },
    [persistChanges]
  );

  const onMaxValueChanged = useCallback(
    (value: string) => {
      const num = parseInt(value);
      const updatedValue = _.isFinite(num) ? num : undefined;
      setRange((prevState) => {
        const newState: NumberRange = {
          ...prevState,
          max: updatedValue,
        };
        persistChanges(newState);
        return newState;
      });
    },
    [persistChanges]
  );

  const isInvalid =
    range.max !== undefined && range.min !== undefined && range.max < range.min;

  const isInteger = /^\d+$/;

  return (
    <Flex gap="2" flex={2} alignItems="center">
      <FilterSelect
        variant="operator"
        options={Object.values(filterSelectionOptions)}
        value={filterSelectionOptions.btw}
      />
      <NumberInput
        inputMode="numeric"
        isInvalid={isInvalid}
        isValidCharacter={(value) => isInteger.test(value)}
        min={0}
        onChange={onMinValueChanged}
        value={range.min ?? ''}
      >
        <NumberInputField placeholder="Min" />
      </NumberInput>
      <Text color={'kgray.300'}>and</Text>
      <NumberInput
        inputMode="numeric"
        isInvalid={isInvalid}
        isValidCharacter={(value) => isInteger.test(value)}
        min={0}
        onChange={onMaxValueChanged}
        value={range.max ?? ''}
      >
        <NumberInputField placeholder="Max" />
      </NumberInput>
      <CloseButton onClick={onRemove} />
    </Flex>
  );
};
